/**
 * Le tirage d'événements — SPEC-MOTEUR §10, un seul système (PLAN §4.9).
 *
 * **Un seul tirage par avancée de temps** : à chaque publication et à chaque sommeil (§10.1).
 * Jamais deux événements empilés, et un `pending_event` non résolu bloque tout (arbitrage
 * **A24**, porté par `validateAction`).
 *
 * L'ordre du pseudocode §10.1 est reproduit à la lettre dans `tirerEvenement` :
 *
 * ```
 * 1. publication : le jet viral (§10.2) est DÉJÀ fait par `publier` (`publish.ts`). S'il a
 *    réussi, un EngineEvent `viral` est dans `ctx.events` : la viralité EST l'événement du
 *    tour, on s'arrête là (on ne re-tire JAMAIS le jet viral ici).
 * 2. anti-frustration (§10.4) : au sommeil, si la garantie se déclenche, le tirage est forcé
 *    sur le pool `window`, sans le pseudo-événement « none » (pas 4 sauté).
 * 3. candidats : conditions, moment, cooldown propre (§10.5).
 * 4. pseudo-événement « none » : 65 à la publication, 80 au sommeil (arbitrage **A7**).
 * 5. poids × modulateurs (§10.3, table fermée).
 * 6. UN SEUL `draw(seed, turn, "event_pick")`, roulette sur les poids cumulés (`pickWeighted`).
 * 7. « none » ⇒ rien. Sinon l'événement est posé, et ⟦A-53⟧ ouvre ici **deux issues** :
 *    - il porte des `choix` (93 des 101) ⇒ résolution différée à l'action `choix_evenement`,
 *      c'est le modal bloquant de SPEC-UI §3.6 — le comportement d'avant l'avenant ;
 *    - il porte une `issue` (8 des 101) ⇒ **notification** : effets appliqués sur-le-champ,
 *      récit versé au journal et au récapitulatif ⟦A-16⟧, aucune interruption.
 * 8. plafond −15 % : il est appliqué par `applyEffectsInPlace` (`effects.ts`,
 *    `MAX_PERTE_ABONNES_PCT`) au moment où les effets du choix sont appliqués. Rien n'est
 *    re-implémenté ici.
 * 9. `major: true` ⇒ +1 PC « événement majeur survécu » à la résolution complète (§9.3).
 * ```
 *
 * Pureté (CLAUDE.md) : aucune I/O, aucun `Math.random`, aucune horloge système. Le contenu
 * arrive **en paramètre** (arbitrage **A25**) et le temps est `state.clock_h` en heures
 * éveillées. Purposes RNG consommés : `event_pick` uniquement (§3.2) — voir la note
 * « purposes » plus bas pour `event_choice_outcome` et `event_params`, qu'aucun champ du
 * contenu v1 n'alimente.
 *
 * Convention de mutation (celle de `curves.ts` et de `sleep.ts`) : les fonctions de ce
 * fichier **mutent** l'état qu'on leur passe — `ctx.state` est déjà le clone de travail de la
 * file §2.3, jamais l'état d'entrée de `resolve`.
 *
 * ⚠️ **État du contenu v1 (constat M1.3/M1.4, redit par SPEC-MOTEUR §10.4)** : aucun des
 * 30 événements ne porte `window: true` ni `major: true`. Le pool forcé de l'anti-frustration
 * est donc **vide** — le tirage forcé retombe proprement sur « none » — et le « +1 PC
 * événement majeur » est **inerte**. Les deux mécaniques sont écrites et testées sur
 * **fixtures** ; le contenu taggé arrive en M3.5.
 */
import { EVENT_COOLDOWN_H_DEFAUT } from "../content/schema.js";
import { piocherCartes } from "./actions.js";
import { H_STAGNATION, prorataHebdo } from "./clock.js";
import { estMonetise } from "./curves.js";
import { escalierTermine } from "./escalier.js";
import { extinctionTelegraphiee, smsDeTaMere, sponsorDerniereChanceDu } from "./gameover.js";
// ⟦A-74⟧ Lecture SEULE de l'état des PNJ (§7.2bis) : la passation du n°1 se détecte ici, au
// tirage — `rivaux.ts` reste le propriétaire des courbes, on ne fait que les regarder.
import { abonnesRival, narrerRelations, photoRelations, rivalDuContenu, rivaux } from "./rivaux.js";
import { applyEffectsInPlace, collectModifiers, consommerEvenementProgramme, evenementsDus, hasContentFlag, poidsModuleParContenu, } from "./effects.js";
import { conditionProduitPasse } from "./axe-produit.js";
import { nicheReconnue } from "./niche.js";
import { PALIER_MILLION, crediterEvenementMajeur } from "./progress.js";
import { managerChoisi, managerTranche } from "./manager.js";
import { evenementsApresPublications, evenementsDesactives, evenementsTirables, pasDEvenementALaVictoire, systemes, } from "./menage.js";
import { draw, pickWeighted } from "./rng.js";
import { opportuniteOuverte, opportunites } from "./opportunites.js";
import { contenuSkills } from "./skills.js";
import { InvalidAction, } from "./types.js";
/**
 * Extrait les événements d'un contenu moteur qui les porte. `EngineContent` (`types.ts`) ne
 * déclare **pas encore** le champ `events` — le fichier est édité par une autre tâche, on ne
 * s'y invite pas : la lecture est **élargie**, jamais castée en `any` (même shim que
 * `contenuSkills` de M1.14 et `skillsDuContenu` de M1.11). **Écart signalé** dans le rapport.
 *
 * Contenu sans événements ⇒ pool vide, donc « none » à chaque tirage : l'absence est visible
 * dans les tests plutôt que silencieusement permissive.
 */
export function evenementsDuContenu(content) {
    const porteur = content;
    return { events: porteur.events ?? [] };
}
/** Un événement par son id (`null` si le contenu ne le porte pas). */
export function evenementParId(content, eventId) {
    return evenementsDuContenu(content).events.find((e) => e.id === eventId);
}
// ─────────────────────────────────────────────────────────────────────────────
// Constantes normatives
// ─────────────────────────────────────────────────────────────────────────────
/**
 * Poids du pseudo-événement « none » — arbitrage **A7** : les poids de contenu sont normés
 * **1-12** (échelle livrée), « none » reste à **65 / 80** (§10.1 pas 4).
 */
export const POIDS_NONE_PUBLICATION = 65;
export const POIDS_NONE_SOMMEIL = 80;
/** Id réservé du pseudo-événement — aucun événement de contenu ne peut le porter. */
export const ID_NONE = "none";
/** §10.3 — exécution ≥ 90 de la dernière vidéo : `good_buzz` ×1,5 (PLAN §4.5). */
export const SEUIL_EXECUTION_GOOD_BUZZ = 90;
export const MOD_GOOD_BUZZ = 1.5;
/**
 * §10.3 — exécution < 30 : ×2 sur `montage_rate_meme` (correction M1.8 : SPEC-MOTEUR visait
 * `bad_buzz_meme_montage`, qui n'existe pas ; l'id livré par `content/events.json` fait foi).
 */
export const SEUIL_EXECUTION_MONTAGE_RATE = 30;
export const EVENT_MONTAGE_RATE = "montage_rate_meme";
export const MOD_MONTAGE_RATE = 2;
/**
 * §10.3 — seuils d'image de chaîne : arbitrage **A8**, ce sont ceux du **MOTEUR**
 * (−20 / −40 / +20), pas ceux de SPEC-CONTENU (−40 / −70 / +40) : la dérive est punie
 * progressivement.
 */
export const IMAGE_DERIVE_LEGERE = -20;
export const IMAGE_DERIVE_FORTE = -40;
export const IMAGE_LUMIERE = 20;
export const MOD_DERIVE_LEGERE = 1.5;
/** Remplace (ne cumule pas avec) `MOD_DERIVE_LEGERE` sous −40 (§10.3 : « remplace le ×1,5 »). */
export const MOD_DERIVE_FORTE = 2;
export const MOD_OPPORTUNITE = 1.5;
/** Catégories visées par les modulateurs d'image (§10.3). */
export const CATEGORIES_DERIVE = ["bad_buzz", "drama"];
export const CATEGORIE_OPPORTUNITE = "opportunite";
export const CATEGORIE_GOOD_BUZZ = "good_buzz";
/** §10.3 — zone rouge de SM (§11.1) : les négatifs ×2 (PLAN §4.10). */
export const SM_ROUGE_MIN = 1;
export const SM_ROUGE_MAX = 29;
export const MOD_SM_ROUGE_NEGATIF = 2;
/** §10.3 — mode hardcore : les négatifs ×1,2 (PLAN §4.11 « +20 % »). */
export const MOD_HARDCORE_NEGATIF = 1.2;
/**
 * §9.4bis ⟦A-58⟧ — **le télégraphiage du Fantôme**, 9e ligne de la table §10.3.
 *
 * *« À mi-chemin, le Fil se vide et les événements positifs cessent »* (SPEC-MOTEUR §9.4bis).
 * Le Fil vit ailleurs ; ce que le moteur peut tenir, c'est la seconde moitié de la phrase —
 * `good_buzz` et `opportunite` passent à **×0**, donc disparaissent de la roulette.
 *
 * ⚠️ C'est **une ligne ajoutée à une table déclarée fermée**, et elle est assumée comme telle :
 * sans elle, la mort la plus lente du jeu serait aussi la seule qui ne se voit pas venir. Elle
 * est **neutre par défaut** ⟦A-10⟧ — `extinctionTelegraphiee` est fausse pour toute chaîne qui
 * n'a pas 5 vidéos nulles d'affilée, le momentum au plancher et 30 jours de rente morte.
 */
export const MOD_EXTINCTION_POSITIF = 0;
/**
 * §10.3 — contre-mesure possédée : « divise son poids par 2 ✎ (ou annule l'événement si le
 * contenu le spécifie) ». Le contenu porte la donnée exacte dans `contremesure.poids_mult`
 * (0,5 · 0,6 · 0,7 · 1,0 selon l'événement) : c'est **elle** qui s'applique — « Contenu =
 * données » (CLAUDE.md), même logique que les arbitrages A18/A23/A26/A27. Ce ÷2 n'est donc
 * que le **défaut** d'une contre-mesure qui n'annoncerait pas son multiplicateur.
 * **Écart signalé** dans le rapport de M1.15.
 */
export const CONTREMESURE_MULT_DEFAUT = 0.5;
/**
 * §10.4 — croissance hebdomadaire sous laquelle la chaîne est jugée stagnante : **1 %** ✎.
 * Couplé à `H_STAGNATION` (336 h = 3 semaines simulées sans palier, `clock.ts`).
 */
export const CROISSANCE_HEBDO_STAGNATION = 0.01;
/**
 * Préfixes réservés des marqueurs système posés dans `state.content_effects.flags` (le seul
 * magasin daté et sérialisable dont le moteur dispose sans toucher à `types.ts`, édité par une
 * autre tâche — **écart signalé**) :
 *
 *  - `cd:<event_id>` — cooldown propre d'un événement (§10.5) : posé au tirage, expire tout
 *    seul, purgé par `purgerEffetsExpires`.
 *  - `sys:antifrustration` — chrono de la garantie §10.4, remis à zéro à chaque déclenchement.
 *
 * Le `:` est **interdit** dans un id de contenu (`Id = /^[a-z0-9_]+$/`, SPEC-CONTENU §2) :
 * aucune collision n'est possible avec un flag de contenu, et aucune condition de contenu ne
 * peut lire ces marqueurs.
 */
export const PREFIXE_COOLDOWN = "cd:";
export const FLAG_ANTIFRUSTRATION = "sys:antifrustration";
// ─────────────────────────────────────────────────────────────────────────────
// Flags — arbitrage A28 : il y a DEUX registres, plus les flags dérivés de §5.7
// ─────────────────────────────────────────────────────────────────────────────
/**
 * Flags **moteur dérivés** de SPEC-CONTENU §5.7 : le contenu ne peut que les LIRE, et ils ne
 * sont stockés nulle part — ce sont des prédicats sur l'état. C'est bien ce registre-là que
 * `content/events.json` utilise (`monetise`, `a_evergreen_active`, `a_nulle_visible`), pas les
 * clés de `GameState.flags` : lire un seul des registres serait un bug (A28).
 */
export const FLAGS_MOTEUR_DERIVES = [
    "monetise",
    "a_evergreen_active",
    "a_nulle_visible",
    "tuto_termine",
    // ⟦A-27 · A-34⟧ Le genre de l'avatar. Ces deux-là ne sont pas des flags stockés : ce sont
    // les prédicats `state.genre === "f"` / `=== "h"`. Une run `genre: null` rend `false` aux
    // DEUX — c'est l'état neutre ⟦A-10⟧, et c'est ce qui garde les 20 événements genrés hors
    // du pool des runs sans genre, donc les 12 ancres intactes.
    "genre_f",
    "genre_h",
];
/** `a_evergreen_active` (§5.7) : « ≥ 1 vidéo evergreen active en bibliothèque ». */
function aEvergreenActive(state) {
    return state.videos.some((v) => v.class === "evergreen" && v.evergreen !== null && v.visibility === "public");
}
/** `a_nulle_visible` (§5.7) : « ≥ 1 vidéo nulle visible » (même lecture que `publish.ts`). */
function aNulleVisible(state) {
    return state.videos.some((v) => v.class === "nulle" && v.visibility === "public");
}
/** Valeur d'un flag moteur dérivé (§5.7). */
export function flagMoteurDerive(state, flag) {
    switch (flag) {
        // `estMonetise` (`curves.ts`) est la seule source de vérité du seuil : abonnés ≥ 1 000 ET
        // Grand Saut fait (avenant A-1 — « le flag est le commutateur » de la couche argent).
        case "monetise":
            return estMonetise(state);
        case "a_evergreen_active":
            return aEvergreenActive(state);
        case "a_nulle_visible":
            return aNulleVisible(state);
        // ⟦A-5 / A-9⟧ Calculé, plus lu dans l'état : `flags.tutorial_done` n'avait **aucun
        // écrivain dans le dépôt**, donc ce drapeau valait `false` pour toujours. Voir
        // `escalierTermine`.
        case "tuto_termine":
            return escalierTermine(state);
        // ⟦A-27⟧ `state.genre` est la seule source : `null` ⇒ `false` des deux côtés.
        case "genre_f":
            return state.genre === "f";
        case "genre_h":
            return state.genre === "h";
    }
}
/**
 * Un flag est-il posé ? **Arbitrage A28** — les trois lectures, dans cet ordre :
 *
 *  1. les 4 **flags moteur dérivés** de SPEC-CONTENU §5.7 (`monetise`…) ;
 *  2. les clés booléennes de **`state.flags`** (enum fermé du moteur, §1.5) ;
 *  3. les **flags de contenu** datés (`state.content_effects.flags`, via `hasContentFlag`).
 *
 * Lire un seul de ces registres est un bug : `content/events.json` s'appuie sur (1) et (3),
 * et rien n'interdit à un contenu futur de lire (2) — `grand_saut_done`, `arc_chute_active`…
 */
export function flagPose(state, flag) {
    if (FLAGS_MOTEUR_DERIVES.includes(flag)) {
        return flagMoteurDerive(state, flag);
    }
    const moteur = state.flags[flag];
    if (typeof moteur === "boolean")
        return moteur;
    return hasContentFlag(state, flag);
}
// ─────────────────────────────────────────────────────────────────────────────
// §10.1 pas 3 — les conditions d'un candidat
// ─────────────────────────────────────────────────────────────────────────────
/** La dernière vidéo publiée (turn le plus élevé, puis id) — `null` si aucune. */
export function derniereVideo(state) {
    let derniere = null;
    for (const v of state.videos) {
        if (derniere === null ||
            v.published_turn > derniere.published_turn ||
            (v.published_turn === derniere.published_turn && v.id > derniere.id)) {
            derniere = v;
        }
    }
    return derniere;
}
/** Valeur courante d'une jauge de contenu (`GaugeId`) — `null` si le nom est inconnu. */
function valeurJauge(state, gauge) {
    switch (gauge) {
        case "energie":
            return state.energy;
        case "sante_mentale":
            return state.mental;
        case "reputation":
            return state.reputation;
        case "fidelite":
            return state.loyalty;
        case "argent":
            return state.money;
        case "momentum":
            return state.momentum;
        case "inspiration":
            return state.inspiration.length;
        default:
            return null;
    }
}
/** Le moment autorisé par le `declencheur` du contenu (§10.1 pas 3). */
export function momentAutorise(event, moment) {
    return event.declencheur === "les_deux" || event.declencheur === moment;
}
/**
 * Toutes les conditions de l'événement passent-elles (§10.1 pas 3) ? Le `moment` n'est PAS
 * testé ici (voir `momentAutorise`), le cooldown non plus (voir `enCooldown`) : chaque filtre
 * est isolé pour rester testable un par un.
 *
 * ✎ `execution_min` / `execution_max` portent sur le score d'exécution de la **dernière
 * vidéo** (§10.1 pas 3, PLAN §4.5). **Sans aucune vidéo publiée, la condition ne passe pas** :
 * `montage_rate_meme` (`execution_max: 49`) ne peut pas sortir avant la première publication.
 *
 * ✎ SPEC-MOTEUR §10.1 mentionne aussi « classe/format de la dernière vidéo si exigés » : le
 * schéma fermé de `EventConditions` (SPEC-CONTENU §3.3, complété par A9) n'a **aucun champ**
 * pour les exprimer — il n'y a donc rien à filtrer. **Écart signalé.**
 */
export function conditionsPassent(state, event) {
    const c = event.conditions;
    if (!c)
        return true;
    if (c.subs_min !== undefined && state.subscribers < c.subs_min)
        return false;
    if (c.subs_max !== undefined && state.subscribers > c.subs_max)
        return false;
    // ⟦A-27⟧ La condition de genre. `state.genre === null` ne satisfait NI `"f"` NI `"h"` : une
    // run sans genre joue exactement le jeu d'avant l'avenant. Le contenu porte encore le doublon
    // `flags_requis: ["genre_f"]`, qui exprime le même prédicat — redondant, jamais contradictoire.
    if (c.genre_requis !== undefined && state.genre !== c.genre_requis)
        return false;
    for (const flag of c.flags_requis ?? [])
        if (!flagPose(state, flag))
            return false;
    for (const flag of c.flags_interdits ?? [])
        if (flagPose(state, flag))
            return false;
    for (const borne of c.jauges ?? []) {
        const valeur = valeurJauge(state, borne.gauge);
        if (valeur === null)
            return false; // jauge inconnue : condition inévaluable ⇒ pas candidat
        if (borne.min !== undefined && valeur < borne.min)
            return false;
        if (borne.max !== undefined && valeur > borne.max)
            return false;
    }
    if (c.image_min !== undefined && state.channel_image < c.image_min)
        return false;
    if (c.image_max !== undefined && state.channel_image > c.image_max)
        return false;
    // ⟦SPEC-MOTEUR §7.4 · A-4 C⟧ La niche de chaîne. `null` (touche-à-tout, ou moins de trois
    // vidéos d'un même thème sur les cinq dernières) ne satisfait AUCUNE liste : un événement
    // « pas pour toi, cette niche » n'a rien à dire à quelqu'un qui n'en a pas encore.
    if (c.niche_requise !== undefined) {
        const niche = nicheReconnue(state);
        if (niche === null || !c.niche_requise.includes(niche))
            return false;
    }
    // ⟦SPEC-AXE-PRODUIT §6.1⟧ L'axe Produit ↔ Authentique. Le prédicat vit dans
    // `engine/axe-produit.ts` — c'est lui qui sait que `null` n'est pas « zéro » mais « pas de
    // mesure », donc que la condition est INÉVALUABLE et écarte le candidat (comme
    // `execution_min` sans vidéo publiée, juste en dessous).
    if (!conditionProduitPasse(state, c))
        return false;
    if (c.execution_min !== undefined || c.execution_max !== undefined) {
        const derniere = derniereVideo(state);
        if (derniere === null)
            return false;
        if (c.execution_min !== undefined && derniere.execution_score < c.execution_min)
            return false;
        if (c.execution_max !== undefined && derniere.execution_score > c.execution_max)
            return false;
    }
    return true;
}
/**
 * Au moins un choix est-il **disponible** ? Un événement dont TOUS les choix sont verrouillés
 * (skill ou flag manquant) ne peut pas être résolu : il bloquerait la run pour de bon
 * (arbitrage A24). Il est donc écarté du pool — garde-fou, aucun événement v1 n'est dans ce
 * cas (chacun a au moins un choix libre).
 */
export function aUnChoixDisponible(state, event) {
    const choix = event.choix ?? [];
    if (choix.length === 0)
        return true; // notification ⟦A-53⟧ (ou fixture) : rien à débloquer
    return choix.some((c) => choixDisponible(state, c));
}
/** Un choix est-il disponible (SPEC-UI §3.6 : sinon grisé avec sa condition) ? */
export function choixDisponible(state, choix) {
    if (choix.skill_requis !== undefined && !state.skills.includes(choix.skill_requis))
        return false;
    if (choix.flag_requis !== undefined && !flagPose(state, choix.flag_requis))
        return false;
    return true;
}
// ─────────────────────────────────────────────────────────────────────────────
// §10.5 — cooldown propre par événement
// ─────────────────────────────────────────────────────────────────────────────
/** Cooldown de l'événement en heures simulées — `EVENT_COOLDOWN_H_DEFAUT` (480 h ✎) par défaut. */
export function cooldownDe(event) {
    return event.cooldown_h ?? EVENT_COOLDOWN_H_DEFAUT;
}
/** L'événement est-il encore en cooldown (§10.5) ? */
export function enCooldown(state, event) {
    return hasContentFlag(state, PREFIXE_COOLDOWN + event.id);
}
/**
 * Pose un flag système daté dans le sidecar de contenu, via le seul chemin d'écriture public
 * (`applyEffectsInPlace` + `flag_add`) : rien n'est réécrit à la main. MUTE `state`.
 */
function poserFlagSysteme(state, flag, dureeH) {
    applyEffectsInPlace(state, [{ type: "flag_add", flag, expires_h: dureeH }], "systeme");
}
/** Démarre le cooldown de l'événement au `clock_h` courant (§10.5). MUTE `state`. */
export function marquerCooldown(state, event) {
    const duree = cooldownDe(event);
    if (duree > 0)
        poserFlagSysteme(state, PREFIXE_COOLDOWN + event.id, duree);
}
/**
 * Poids effectif d'un candidat : contenu (`event_weight_mult_*`) × modulateurs §10.3 ×
 * contre-mesure possédée.
 *
 * `execution` est le score d'exécution de la **dernière vidéo** (`null` hors publication ou
 * sans vidéo). Les deux premières lignes de la table §10.3 ne s'appliquent donc qu'à un
 * tirage de publication, ce qui est bien leur sens (« dernière vidéo »).
 */
export function poidsModule(state, mods, event, execution) {
    const base = poidsModuleParContenu(mods, {
        id: event.id,
        categorie: event.categorie,
        poids: event.poids,
    });
    let m = 1;
    // — exécution de la dernière vidéo (PLAN §4.5) —
    if (execution !== null) {
        if (execution >= SEUIL_EXECUTION_GOOD_BUZZ && event.categorie === CATEGORIE_GOOD_BUZZ) {
            m *= MOD_GOOD_BUZZ;
        }
        if (execution < SEUIL_EXECUTION_MONTAGE_RATE && event.id === EVENT_MONTAGE_RATE) {
            m *= MOD_MONTAGE_RATE;
        }
    }
    // — image de chaîne (PLAN §4.7), seuils du MOTEUR : arbitrage A8 —
    const derive = CATEGORIES_DERIVE.includes(event.categorie);
    if (derive) {
        // « ≤ −40 remplace le ×1,5 » (§10.3) : jamais 1,5 × 2.
        if (state.channel_image <= IMAGE_DERIVE_FORTE)
            m *= MOD_DERIVE_FORTE;
        else if (state.channel_image <= IMAGE_DERIVE_LEGERE)
            m *= MOD_DERIVE_LEGERE;
    }
    if (event.categorie === CATEGORIE_OPPORTUNITE && state.channel_image >= IMAGE_LUMIERE) {
        m *= MOD_OPPORTUNITE;
    }
    // — négatifs : zone rouge de SM (PLAN §4.10) et hardcore (PLAN §4.11) —
    if (event.negatif === true) {
        if (state.mental >= SM_ROUGE_MIN && state.mental <= SM_ROUGE_MAX)
            m *= MOD_SM_ROUGE_NEGATIF;
        if (state.mode === "hardcore")
            m *= MOD_HARDCORE_NEGATIF;
    }
    // — §9.4bis ⟦A-58⟧ extinction télégraphiée : les événements positifs cessent —
    if ((event.categorie === CATEGORIE_GOOD_BUZZ || event.categorie === CATEGORIE_OPPORTUNITE) &&
        extinctionTelegraphiee(state)) {
        m *= MOD_EXTINCTION_POSITIF;
    }
    // — ⟦A-27 · A-4 décision 2⟧ pondération différenciée par genre —
    //   Placée avec les autres modulateurs §10.3, donc AVANT la contre-mesure, qui reste le
    //   dernier mot du joueur. `genre: null` ⇒ aucune clé ne s'applique ⇒ ×1 : l'état neutre.
    if (state.genre !== null && event.poids_genre !== undefined) {
        m *= event.poids_genre[state.genre] ?? 1;
    }
    // — contre-mesure possédée : le multiplicateur du CONTENU, ÷2 par défaut —
    let contremesure = 1;
    if (event.contremesure && state.skills.includes(event.contremesure.skill_id)) {
        contremesure = event.contremesure.poids_mult ?? CONTREMESURE_MULT_DEFAUT;
    }
    return {
        event_id: event.id,
        base,
        modulateurs: m,
        contremesure,
        poids: base * m * contremesure,
    };
}
/** À appeler sur l'état **d'entrée** de `resolve`, avant `applyAction` (voir ci-dessus). */
export function capturerReference(state) {
    return { subs: state.counters.subs_at_last_sleep, clock_h: state.counters.last_sleep_h };
}
/**
 * Croissance hebdomadaire pro rata (§10.4) : `(abonnés − référence) / référence`, ramené à
 * une semaine simulée (112 h, `prorataHebdo`). Rend `null` quand elle n'est pas mesurable —
 * aucune heure écoulée depuis la référence, ou référence à 0 abonné (division impossible).
 */
export function croissanceHebdo(state, reference) {
    const deltaH = state.clock_h - reference.clock_h;
    if (deltaH <= 0)
        return null;
    if (reference.subs <= 0)
        return null;
    const croissance = (state.subscribers - reference.subs) / reference.subs;
    return croissance / prorataHebdo(deltaH);
}
/** Heures écoulées depuis le dernier palier franchi (§10.4). */
export function heuresSansPalier(state) {
    return state.clock_h - state.counters.last_milestone_h;
}
/**
 * La garantie anti-frustration se déclenche-t-elle (§10.4) ? Trois conditions :
 * **aucun palier depuis 336 h** ✎ ET **croissance hebdo mesurable < 1 %** ✎ ET la garantie
 * n'a pas déjà servi dans les 336 dernières heures (« reset du chrono après déclenchement »).
 */
export function stagnationDetectee(state, reference) {
    if (hasContentFlag(state, FLAG_ANTIFRUSTRATION))
        return false;
    if (heuresSansPalier(state) < H_STAGNATION)
        return false;
    const croissance = croissanceHebdo(state, reference);
    if (croissance === null)
        return false;
    return croissance < CROISSANCE_HEBDO_STAGNATION;
}
// ─────────────────────────────────────────────────────────────────────────────
// ⟦SPEC-MOTEUR §7.2bis · A-4 arbitrage D · A-74⟧ La passation du n°1 de MeTube
// ─────────────────────────────────────────────────────────────────────────────
/**
 * L'événement de contenu que la passation pose — une notification ⟦A-53⟧, parce qu'on ne
 * choisit pas ce moment-là. ⚠️ Entorse assumée à « contenu = données », la même que
 * `JALON_EXCUSES` (`jalons.ts`) et les nœuds de fin (`fins.ts`) : un id de contenu lu par le
 * moteur, en constante exportée et testée contre `content/events.json`. Absent du dossier de
 * contenu ⇒ aucune passation n'est jamais signalée — l'état neutre par construction.
 */
export const EVENEMENT_PASSATION = "passation_numero_1";
/**
 * ✎ Taille minimale du sommet battu pour que la passation vaille d'être racontée : passer
 * devant un PNJ de 300 abonnés n'est pas « devenir le n°1 de MeTube ». Avec le vivier réel
 * (`content/rivaux.json`, plafonds 30 k → 250 k), la passation tombe en fin de course —
 * le dernier grand rendez-vous avant le million.
 */
export const PASSATION_PLANCHER = 10_000;
/**
 * ⟦§7.2bis⟧ **Le classement des PNJ entre eux, joueur compris** — la lecture qui manquait :
 * les « courses aux paliers » avaient l'écart un à un (`rivauxLisibles`), jamais l'ordre.
 * Pure : deux appels au même instant rendent le même tableau. Ordre décroissant, et à
 * égalité parfaite le joueur d'abord — c'est son écran.
 */
export function classementMeTube(state, content) {
    const lignes = [
        { id: null, nom: "", abonnes: Math.round(state.subscribers), joueur: true },
        ...rivaux(state).map((r) => ({
            id: r.id,
            nom: rivalDuContenu(content, r.id)?.nom ?? r.id,
            abonnes: abonnesRival(r, state.clock_h),
            joueur: false,
        })),
    ];
    return lignes.sort((a, b) => b.abonnes - a.abonnes || Number(b.joueur) - Number(a.joueur));
}
/**
 * ⟦C62 bis · A-179⟧ Cette action va-t-elle GRAVER la victoire ? C'est la condition de `progressionEtFins` (pas 10 : le pic
 * d'abonnés de l'action, `HookContext.picAbonnes`, ou ceux de la fin touchent le million, `victory_locked` encore faux),
 * lue au tirage (pas 7-8), après le dernier pas qui fait bouger les abonnés (effet de l'action, nuit, rente). Sans pic noté
 * (un contexte monté à la main), les abonnés de la fin seuls : le jeu d'avant le pic.
 */
function graveLaVictoire(ctx) {
    const { state } = ctx;
    return !state.victory_locked && Math.max(state.subscribers, ctx.picAbonnes ?? 0) >= PALIER_MILLION;
}
function sansEvenement(raison, forcee = false) {
    return { event_id: null, raison, forcee, candidats: [], u: null, pending: false };
}
/**
 * Le hook `tirerEvenement` de `ResolutionHooks` (§2.3 pas 7-8) — **le** point d'entrée de
 * §10.1. MUTE `ctx.state` (déjà le clone de travail de la file).
 *
 * Purposes RNG : `event_pick` seulement, et **un seul** `draw` par tirage (§10.1 pas 6). Le
 * jet viral appartient à `publier` (§10.2) : on ne le refait pas, on lit son `EngineEvent`.
 */
export function tirerEvenement(ctx, moment, options = {}) {
    const state = ctx.state;
    // Garde-fous : une run finie ne tire plus, et jamais deux événements empilés (A24).
    if (state.status === "game_over")
        return sansEvenement("run_terminee");
    // ⟦PLAN §4.10 · A-74⟧ **« SMS de ta mère »** — posé ICI parce que le tirage est le moment
    // où le monde extérieur parle au joueur (publication et sommeil, les deux mêmes instants).
    // Avant le garde `pending` : que le joueur ait une tuile à trancher n'empêche pas sa mère
    // de s'inquiéter. Aucun tirage consommé, aucune jauge touchée — voir `smsDeTaMere`.
    smsDeTaMere(state, ctx.events);
    if (state.pending_event !== null)
        return sansEvenement("pending");
    // ⟦C62 · lot moteur · A-179⟧ **Pas d'événement avant que le joueur ait vu les gestes.** L'audit « regard neuf »
    // du 21/10 : trois événements en trois vidéos, avec des choix verrouillés que personne ne pouvait lire.
    // `meta.evenements_apres_publications` (défaut 0 = le jeu d'avant, au caractère près) fixe le
    // nombre de publications à avoir faites ; tant qu'il n'est pas atteint, RIEN ne se pose : ni la roulette,
    // ni la viralité, ni un rendez-vous programmé, ni la passation (ils attendent, ils ne sont pas perdus :
    // `scheduled_events` n'est pas consommé). Placé AVANT tout `draw` : aucun tirage consommé, et les tirages
    // suivants (indexés par tour, voir `rng.ts`) ne bougent pas.
    const publicationsRequises = evenementsApresPublications(ctx.content);
    if (publicationsRequises > 0 && state.counters.videos_published < publicationsRequises) {
        return sansEvenement("trop_tot");
    }
    // ⟦C62 bis · A-179⟧ **Pas d'événement par-dessus « LE MILLION ».** Matt (22/10) : l'événement tiré la nuit du
    // million s'affichait sur l'écran de la victoire, et le moteur interdit toute autre action tant qu'un événement
    // attend. Même porte que la précédente, au même endroit, pour la même raison : AVANT tout `draw`, donc aucun tirage
    // consommé, et ni la viralité, ni un rendez-vous programmé, ni la passation ne sont posés ni perdus (`scheduled_events`
    // n'est pas touché). Le tirage reprend à l'action suivante, victoire gravée (« Continuer » vers le 100 %). La condition
    // est celle de la gravure (`progressionEtFins`, pas 10) lue un pas plus tôt : les abonnés de la fin OU le pic de
    // l'action (`ctx.picAbonnes`, que la nuit ne peut plus reprendre) touchent le million, victoire pas encore gravée.
    // Les événements-notifications qui rendent des abonnés (pas 7-8) ne peuvent donc plus défaire ce que la porte constate.
    if (pasDEvenementALaVictoire(ctx.content) && graveLaVictoire(ctx)) {
        return sansEvenement("victoire");
    }
    const contenu = evenementsDuContenu(ctx.content);
    // — pas 1 : le jet viral EST l'événement du tour (§10.2) —
    //
    // ⟦A-74⟧ **Et l'événement de catégorie `viralite` est enfin CET événement-là.** SPEC-CONTENU
    // §3.3 : « l'événement de catégorie viralite n'est jamais tiré au pool : il est déclenché
    // par le jet viral 3 % de la formule ». La moitié « jamais au pool » était tenue
    // (`tirable: false`) ; la moitié « déclenché par le jet » ne l'a jamais été : le tirage
    // était simplement SAUTÉ, et le choix Surfer / Savourer n'existait pour personne.
    //
    // Déclenchement GARANTI, hors roulette, comme un `event_schedule` dû : aucun `draw`
    // consommé (la branche sortait déjà avant le `draw`, elle continue de le faire). La
    // catégorie est le sélecteur — « contenu = données », pas d'id en dur : un dossier sans
    // événement `viralite` garde exactement le comportement d'avant. 🔴 Rupture d'état neutre
    // assumée ⟦A-68⟧ : dans les runs où un jet viral passe, l'événement se pose désormais —
    // mesurée dans A-74, jamais subie.
    if (moment === "publication" && ctx.events.some((e) => e.kind === "viral")) {
        const viralite = contenu.events.find((e) => e.categorie === "viralite");
        if (!viralite)
            return sansEvenement("viral");
        return poserEvenement(ctx, viralite, "viral", [], null, false);
    }
    // — `event_schedule` dû : déclenchement GARANTI (SPEC-CONTENU §2.3, §10.5) —
    // Hors roulette, donc aucun `draw` consommé : c'est un rendez-vous, pas un tirage. Le
    // `declencheur` reste respecté — c'est ce qui réalise « l'effet s'applique au premier
    // sommeil après l'échéance » (§10.5, changement d'algorithme).
    // ⟦C60 · A-177⟧ Un rendez-vous dont l'événement appartient à un système COUPÉ est rayé, pas
    // seulement ignoré : il ne reviendrait jamais, et resterait dans `scheduled_events` pour toujours.
    const desactives = evenementsDesactives(ctx.content);
    for (const du of evenementsDus(state)) {
        if (desactives.has(du.event_id)) {
            consommerEvenementProgramme(state, du.event_id);
            continue;
        }
        const event = contenu.events.find((e) => e.id === du.event_id);
        if (!event || !momentAutorise(event, moment))
            continue;
        consommerEvenementProgramme(state, du.event_id);
        return poserEvenement(ctx, event, "programme", [], null, false);
    }
    // — ⟦SPEC-MOTEUR §7.2bis · A-4 arbitrage D · A-74⟧ **la passation du n°1 de MeTube** —
    //
    // Le rendez-vous que les PNJ « nourrissent » d'après la spec, et que rien ne détectait : le
    // sommeil où le joueur passe devant le DERNIER PNJ qui le dominait. Un rendez-vous d'état,
    // pas un tirage — hors roulette, aucun `draw`, une seule fois par run (`passation_faite`).
    //
    //  - `PASSATION_PLANCHER` : pas de « trône » à 300 abonnés — la passation ne se signale que
    //    si le sommet battu voulait dire quelque chose. Un vivier de PNJ ridiculement bas rend
    //    donc le comportement d'avant : rien.
    //  - L'événement est du CONTENU (`EVENEMENT_PASSATION`, une notification ⟦A-53⟧ : on ne
    //    choisit pas ce moment-là, on le vit) — un dossier qui ne le porte pas garde le jeu
    //    d'avant, c'est l'état neutre par construction.
    //  - 🔴 Dans les runs qui montent assez haut, ce sommeil-là ne passe plus par la roulette :
    //    rupture d'état neutre assumée ⟦A-68⟧, mesurée dans A-74 avec la viralité ci-dessus.
    if (moment === "sommeil" && state.flags.passation_faite !== true) {
        const event = evenementsTirables(contenu.events, ctx.content).find((e) => e.id === EVENEMENT_PASSATION);
        const pnj = rivaux(state);
        if (event && pnj.length > 0) {
            const sommet = Math.max(...pnj.map((r) => abonnesRival(r, state.clock_h)));
            if (sommet >= PASSATION_PLANCHER && state.subscribers > sommet) {
                state.flags.passation_faite = true;
                return poserEvenement(ctx, event, "passation", [], null, false);
            }
        }
    }
    // — ⟦C7⟧ pas 1bis : le **sponsor dernière chance** (§11.5, PLAN §4.11) —
    //
    // *« alertes J−30/−15/−7 + un sponsor dernière chance GARANTI »*. `sponsorDerniereChanceDu`
    // était exportée depuis M1.16 et **n'avait aucun appelant** : `flags.sponsor_derniere_chance_used`
    // n'était jamais mis à `true`, donc le filet anti-faillite n'existait pas — alors que la
    // simulation mesure que TOUTES les runs qui passent le Grand Saut font faillite.
    //
    // Il passe AVANT l'anti-frustration : une descente vers la faillite est plus urgente qu'un
    // plateau, et les deux ne peuvent pas forcer le même tour.
    //
    // **Le pool est décrit par le CONTENU, pas par des ids en dur** : les événements de
    // catégorie `business` non négatifs. C'est la définition d'un sponsor dans le vocabulaire
    // fermé du schéma (§2.1), donc le contenu peut en ajouter dix sans toucher au moteur.
    const sponsorDu = moment === "sommeil" && sponsorDerniereChanceDu(state, systemes(ctx.content));
    // — pas 2 : anti-frustration (§10.4) —
    const forcee = !sponsorDu &&
        moment === "sommeil" &&
        stagnationDetectee(state, options.reference ?? capturerReference(state));
    if (forcee) {
        // « Reset du chrono après déclenchement » : la garantie est consommée même si le pool est
        // vide ✎ — sinon chaque sommeil suivant relancerait un tirage forcé stérile.
        poserFlagSysteme(state, FLAG_ANTIFRUSTRATION, H_STAGNATION);
    }
    // — pas 3 : les candidats —
    const mods = collectModifiers(state, contenuSkills(ctx.content));
    const derniere = derniereVideo(state);
    const execution = moment === "publication" && derniere !== null ? derniere.execution_score : null;
    // ⟦C60 · A-177⟧ Le pool ne contient pas les événements des systèmes coupés. Aucun système coupé
    // (le défaut) : `evenementsTirables` rend la liste d'origine, sans copie.
    const eligibles = evenementsTirables(contenu.events, ctx.content).filter((e) => e.tirable !== false &&
        (!forcee || e.window === true) &&
        (!sponsorDu || (e.categorie === "business" && e.negatif !== true)) &&
        momentAutorise(e, moment) &&
        !enCooldown(state, e) &&
        conditionsPassent(state, e) &&
        aUnChoixDisponible(state, e));
    // — pas 5 : poids × modulateurs (sautés sur un tirage forcé : « poids normaux » §10.1 pas 2 ;
    // les `event_weight_mult_*` du contenu restent comptés, c'est une donnée, pas un modulateur) —
    const candidats = eligibles.map((e) => {
        if (!forcee && !sponsorDu)
            return poidsModule(state, mods, e, execution);
        const base = poidsModuleParContenu(mods, {
            id: e.id,
            categorie: e.categorie,
            poids: e.poids,
        });
        return { event_id: e.id, base, modulateurs: 1, contremesure: 1, poids: base };
    });
    // — pas 4 : le pseudo-événement « none », sauté sur un tirage forcé (§10.1 pas 2) —
    if (!forcee && !sponsorDu) {
        const poidsNone = moment === "publication" ? POIDS_NONE_PUBLICATION : POIDS_NONE_SOMMEIL;
        candidats.push({
            event_id: ID_NONE,
            base: poidsNone,
            modulateurs: 1,
            contremesure: 1,
            poids: poidsNone,
        });
    }
    // Pool forcé vide (contenu v1 : aucun événement `window`) : on retombe proprement sur
    // « none », sans consommer de tirage et sans jamais boucler.
    if (candidats.length === 0) {
        ctx.log.push({
            turn: state.turn,
            clock_h: state.clock_h,
            text: sponsorDu
                ? "Tu écris à toutes les marques que tu connais. Personne ne répond ce soir."
                : "Ça stagne, et rien ne vient : aucune fenêtre à saisir pour l'instant.",
        });
        // ⟦C7⟧ La garantie sponsor n'est PAS consommée sur un pool vide : « une fois par
        // descente » veut dire une fois qu'elle a servi. L'anti-frustration, elle, se consomme
        // (son chrono est déjà réarmé ci-dessus) — les deux règles diffèrent, et c'est voulu :
        // l'une protège d'un plateau, l'autre d'une mort.
        return { event_id: null, raison: "pool_vide", forcee, candidats, u: null, pending: false };
    }
    // — pas 6 : UN SEUL `draw`, roulette sur les poids cumulés —
    const u = draw(ctx.seed, state.turn, "event_pick", 0);
    const index = pickWeighted(candidats.map((c) => c.poids), u);
    const gagnant = index >= 0 ? candidats[index] : undefined;
    // — pas 7 : « none » ⇒ rien —
    if (!gagnant || gagnant.event_id === ID_NONE) {
        return { event_id: null, raison: "none", forcee, candidats, u, pending: false };
    }
    const event = eligibles.find((e) => e.id === gagnant.event_id);
    if (sponsorDu) {
        // « Une fois par descente » (§11.5) : le drapeau est réarmé par `majFaillite` au retour
        // dans le vert. C'est ici, et seulement ici, qu'il se pose.
        state.flags.sponsor_derniere_chance_used = true;
        ctx.events.push({
            kind: "warning",
            data: { scripted: "sponsor_derniere_chance", event_id: event.id, money: state.money },
        });
    }
    return poserEvenement(ctx, event, sponsorDu ? "sponsor" : forcee ? "forcee" : "pool", candidats, u, forcee || sponsorDu);
}
/**
 * Pose un événement tiré : cooldown démarré, `event_drawn` émis, et résolution **différée**
 * à l'action `choix_evenement` dès qu'il a un choix (§10.1 pas 7).
 *
 * ⟦A-53⟧ **Un événement SANS `choix` ne s'arrête pas là et n'attend rien** : c'est une
 * notification. Ses effets (`issue.effets`) s'appliquent **immédiatement**, exactement comme
 * si le joueur avait cliqué, et son récit part au journal et au récapitulatif ⟦A-16⟧. Il ne
 * pose donc ni `pending_event`, ni opportunité. Une fixture sans `choix` ni `issue` garde le
 * comportement d'avant l'avenant : rien à appliquer, seul son `major` est crédité.
 */
function poserEvenement(ctx, event, raison, candidats, u, forcee) {
    const state = ctx.state;
    marquerCooldown(state, event);
    const aDesChoix = (event.choix?.length ?? 0) > 0;
    // ⟦A-15 §4⟧ **Le seul aiguillage de la mécanique.** Un événement à fenêtre ne bloque pas :
    // il se pose sur la table et le monde continue. Tout le reste — choix, effets, action de
    // résolution — est rigoureusement identique. C'est ce qui fait qu'A-15 §4 « complète le
    // système d'événements » au lieu d'en créer un second.
    const fenetre = aDesChoix ? event.fenetre_h : undefined;
    const estOpportunite = fenetre !== undefined && fenetre > 0;
    const pending = aDesChoix && !estOpportunite;
    if (pending) {
        state.pending_event = { event_id: event.id, drawn_at_turn: state.turn };
    }
    else if (estOpportunite) {
        state.opportunites = [
            ...opportunites(state),
            {
                event_id: event.id,
                drawn_at_h: state.clock_h,
                drawn_at_turn: state.turn,
                expires_h: state.clock_h + fenetre,
            },
        ];
    }
    ctx.events.push({
        kind: "event_drawn",
        data: {
            event_id: event.id,
            categorie: event.categorie,
            negatif: event.negatif === true,
            major: event.major === true,
            forcee,
            programme: raison === "programme",
            pending,
            // ⟦A-15 §4⟧ L'UI en a besoin pour choisir entre le modal bloquant (SPEC-UI §3.6) et la
            // pastille d'offre en attente.
            opportunite: estOpportunite,
            expire_h: estOpportunite ? state.clock_h + fenetre : null,
        },
    });
    // ⟦A-53⟧ **La notification raconte, elle n'annonce pas.** Une tuile bloquante écrit « à toi
    // de trancher » parce que le joueur va lire la suite dans la fenêtre ; ici il n'y a pas de
    // fenêtre, donc la ligne de journal porte l'événement en entier : la situation (`texte`),
    // ce que le personnage en a fait (`issue.label`), et sa `morale` ⟦A-38⟧ s'il en a une.
    // C'est ⟦A-43⟧ appliqué : le jeu dit ce qui se passe, et il le dit UNE fois.
    const notification = !aDesChoix && event.issue !== undefined;
    ctx.log.push({
        turn: state.turn,
        clock_h: state.clock_h,
        text: estOpportunite
            ? `Une porte s'entrouvre : « ${event.nom ?? event.id} ». Tu as le temps d'y penser — pas éternellement.`
            : pending
                ? `Événement : « ${event.nom ?? event.id} ». À toi de trancher.`
                : notification
                    ? [event.texte, event.issue.label, event.morale].filter(Boolean).join(" ")
                    : `Événement : « ${event.nom ?? event.id} ».`,
    });
    // ⟦A-53⟧ Les effets tombent ICI, pas à une action qui ne viendra jamais. Même appel, même
    // plafond −15 %, même `source_id` que `resoudreChoixEvenement` : une notification pèse
    // exactement ce que pesait la branche retenue. Le récapitulatif ⟦A-16⟧ mesure la marche
    // sous la cause `evenement`, déjà au vocabulaire fermé de `recap.ts`.
    if (notification) {
        const relationsAvant = photoRelations(state); // ⟦A-84⟧ même récit que `resoudreChoixEvenement`
        const r = ctx.recap
            ? ctx.recap.mesurer("evenement", () => applyEffectsInPlace(state, event.issue.effets, "event", {
                source_id: event.id,
                systemes: systemes(ctx.content),
            }), event.id)
            : applyEffectsInPlace(state, event.issue.effets, "event", {
                source_id: event.id,
                systemes: systemes(ctx.content),
            });
        ctx.events.push(...r.events);
        ctx.log.push(...r.log);
        for (const text of narrerRelations(relationsAvant, state, ctx.content)) {
            ctx.log.push({ turn: state.turn, clock_h: state.clock_h, text });
        }
        // Même dette que le chemin `choix_evenement` (`index.ts` §2) : `applyEffectsInPlace`
        // COMPTE les cartes demandées, il ne les pioche pas. Sans cette ligne, un `gauge_delta`
        // sur `inspiration` serait muet — et `main_tendue` en demande une.
        if (r.cards_to_draw > 0)
            piocherCartes(state, ctx.content, ctx.seed, r.cards_to_draw);
        // §11.2 — l'arc de la chute et les excuses lisent `last_damage` (`gameover.ts`). Aucune
        // notification livrée n'est négative, mais la règle ne doit pas dépendre du contenu.
        if (r.subs_delta < 0 || r.reputation_delta < 0) {
            state.last_damage = {
                event_id: event.id,
                subs_lost: Math.max(0, -r.subs_delta),
                rep_lost: Math.max(0, -r.reputation_delta),
                at_h: state.clock_h,
            };
        }
    }
    if (!aDesChoix && event.major === true)
        crediterEvenementMajeur(state, ctx.events);
    return { event_id: event.id, raison, forcee, candidats, u, pending };
}
/**
 * Garde de l'action `choix_evenement` que `validateAction` ne peut pas porter (il ne reçoit
 * pas les événements : `EngineContent` n'a pas le champ). À appeler par `resolve` (M1.17)
 * **juste après** `validateAction`, avant tout effet. Actions sans rapport : no-op.
 *
 * `validateAction` couvre déjà l'absence de `pending_event` (`no_pending_event`) et le blocage
 * de toute autre action (`pending_event_blocks`, arbitrage A24).
 */
export function validateChoixEvenement(state, action, content) {
    if (action.type !== "choix_evenement")
        return undefined;
    // ⟦A-15 §4⟧ Deux provenances légitimes, et une seule action pour les deux : le
    // `pending_event` bloquant (§3.6) **ou** une opportunité encore ouverte.
    const pending = state.pending_event;
    const offre = opportuniteOuverte(state, action.event_id);
    if (!pending && !offre)
        throw new InvalidAction("no_pending_event");
    if (pending && !offre && pending.event_id !== action.event_id) {
        throw new InvalidAction("event_choice_invalid", {
            event_id: action.event_id,
            attendu: pending.event_id,
        });
    }
    // Une fenêtre déjà refermée ne se rattrape pas — mais on le DIT, plutôt que de laisser
    // l'action passer dans le vide.
    if (offre && state.clock_h >= offre.expires_h) {
        throw new InvalidAction("event_choice_invalid", {
            event_id: action.event_id,
            raison: "opportunite_expiree",
            expires_h: offre.expires_h,
        });
    }
    const event = evenementParId(content, action.event_id);
    if (!event)
        throw new InvalidAction("event_choice_invalid", { event_id: action.event_id });
    const choix = (event.choix ?? []).find((c) => c.id === action.choice_id);
    if (!choix) {
        throw new InvalidAction("event_choice_invalid", {
            event_id: action.event_id,
            choice_id: action.choice_id,
        });
    }
    if (!choixDisponible(state, choix)) {
        throw new InvalidAction("event_choice_invalid", {
            event_id: action.event_id,
            choice_id: action.choice_id,
            requis: choix.skill_requis ?? choix.flag_requis ?? null,
        });
    }
    return { event, choix };
}
/**
 * Applique le choix du joueur : les effets du choix (avec le **plafond −15 %** de
 * `applyEffectsInPlace`), `last_damage` pour l'arc de la chute (§11.2), le +1 PC d'un
 * événement `major` (§9.3), et `pending_event` remis à `null`. MUTE `state`.
 */
export function resoudreChoixEvenement(ctx, action) {
    const state = ctx.state;
    const { event, choix } = validateChoixEvenement(state, action, ctx.content);
    // ⟦A-84⟧ Photo de l'axe relationnel AVANT les effets : si un `rival_relation_delta` l'a
    // déplacé, le journal le dit avec le nom du PNJ — le texte de l'événement, lui, ne parle
    // que d'« un créateur ».
    const relationsAvant = photoRelations(state);
    const r = applyEffectsInPlace(state, choix.effets, "event", {
        source_id: event.id,
        systemes: systemes(ctx.content),
    });
    ctx.events.push(...r.events);
    ctx.log.push(...r.log);
    for (const text of narrerRelations(relationsAvant, state, ctx.content)) {
        ctx.log.push({ turn: state.turn, clock_h: state.clock_h, text });
    }
    // §11.2 — l'arc de la chute et les excuses lisent `last_damage` (`gameover.ts`).
    if (r.subs_delta < 0 || r.reputation_delta < 0) {
        state.last_damage = {
            event_id: event.id,
            subs_lost: Math.max(0, -r.subs_delta),
            rep_lost: Math.max(0, -r.reputation_delta),
            at_h: state.clock_h,
        };
    }
    // ⟦A-15 §4⟧ On ne nettoie que ce qui correspond : répondre à une opportunité ne doit pas
    // effacer un `pending_event` qui n'a rien à voir (et réciproquement).
    if (state.pending_event?.event_id === event.id)
        state.pending_event = null;
    if (opportuniteOuverte(state, event.id)) {
        state.opportunites = opportunites(state).filter((o) => o.event_id !== event.id);
    }
    // §10.1 pas 9 — « à sa résolution complète », donc ici et pas au tirage.
    let pcMajeur = 0;
    if (event.major === true) {
        const avant = state.pc;
        crediterEvenementMajeur(state, ctx.events);
        pcMajeur = state.pc - avant;
    }
    ctx.events.push({
        kind: "event_resolved",
        data: {
            event_id: event.id,
            choice_id: choix.id,
            subs_delta: r.subs_delta,
            subs_loss_capped: r.subs_loss_capped,
            reputation_delta: r.reputation_delta,
            major: event.major === true,
        },
    });
    ctx.log.push({
        turn: state.turn,
        clock_h: state.clock_h,
        text: `Choix : « ${choix.label ?? choix.id} ». C'est fait, on avance.`,
    });
    return {
        event_id: event.id,
        choice_id: choix.id,
        subs_delta: r.subs_delta,
        subs_loss_capped: r.subs_loss_capped,
        reputation_delta: r.reputation_delta,
        pc_effets: r.pc_gained,
        pc_majeur: pcMajeur,
        cards_to_draw: r.cards_to_draw,
        scheduled: r.scheduled,
    };
}
/**
 * ⟦A-50 §3 levier 1 · A-108⟧ **« Je gère »** — reporte le `pending_event` en cours au lieu de
 * le trancher. **Réutilise `resoudreChoixEvenement` telle quelle** (mêmes effets, même plafond
 * −15 %, même `last_damage`, même récapitulatif) : le seul écart tient dans le choix appliqué —
 * le PREMIER choix **disponible** de l'événement, l'« issue par défaut » d'A-50 §3, plutôt que
 * celui que le joueur aurait sélectionné.
 *
 * `validateAction` a déjà vérifié la capacité et la présence d'un `pending_event` — cette
 * fonction ne revalide rien, elle résout. Rend `null` seulement si appelée hors de tout
 * `pending_event` (état impossible en production, gardé pour les appelants de test).
 */
export function resoudreReportEvenement(ctx) {
    const pending = ctx.state.pending_event;
    if (!pending)
        return null;
    const event = evenementParId(ctx.content, pending.event_id);
    if (!event || !event.choix || event.choix.length === 0)
        return null;
    // Le premier choix que le joueur pourrait réellement cliquer — jamais un choix gardé par un
    // skill/flag qu'il n'a pas, qui ferait échouer la résolution en aval (`choixDisponible`,
    // la même garde que le modal d'événement applique déjà pour griser un bouton).
    const parDefaut = event.choix.find((c) => choixDisponible(ctx.state, c)) ?? event.choix[0];
    ctx.events.push({
        kind: "warning",
        data: { cause: "evenement_reporte", event_id: event.id, choice_id: parDefaut.id },
    });
    ctx.log.push({
        turn: ctx.state.turn,
        clock_h: ctx.state.clock_h,
        famille: "evenement",
        text: `Tu remets « ${event.nom ?? event.id} » à plus tard. L'issue par défaut se joue sans toi.`,
    });
    return resoudreChoixEvenement(ctx, {
        type: "choix_evenement",
        event_id: event.id,
        choice_id: parDefaut.id,
    });
}
/**
 * ⟦C8 · A-120⟧ **« Le manager tranche »** — le minuteur CLIENT (opt-in, défaut OFF) a expiré
 * sur le `pending_event` en cours. **Réutilise `resoudreChoixEvenement` telle quelle**, comme
 * `resoudreReportEvenement` juste au-dessus : le seul écart tient dans le choix appliqué —
 * celui que `managerTranche` (`manager.ts`) retient pour la persona recrutée (alignement =
 * style, expérience = qualité), parmi les choix réellement DISPONIBLES (`choixDisponible`,
 * jamais un choix gardé par un skill/flag que le joueur n'a pas).
 *
 * `validateAction` a déjà vérifié qu'un manager est en poste et qu'un `pending_event` existe
 * — cette fonction ne revalide rien, elle résout. Rend `null` seulement si appelée hors de
 * tout `pending_event` (état impossible en production, gardé pour les appelants de test).
 */
export function resoudreTrancheParManager(ctx) {
    const pending = ctx.state.pending_event;
    if (!pending)
        return null;
    const event = evenementParId(ctx.content, pending.event_id);
    if (!event || !event.choix || event.choix.length === 0)
        return null;
    const disponibles = event.choix.filter((c) => choixDisponible(ctx.state, c));
    // Filet : si aucun choix n'était disponible (état de contenu improbable — `aUnChoixDisponible`
    // le garantirait normalement au tirage), on retombe sur la liste entière plutôt que de planter.
    const choix = disponibles.length > 0 ? disponibles : event.choix;
    const manager = managerChoisi(ctx.state, ctx.content);
    const choiceId = managerTranche(ctx.state, choix, manager, ctx.seed);
    ctx.events.push({
        kind: "warning",
        data: {
            cause: "manager_tranche",
            event_id: event.id,
            choice_id: choiceId,
            alignement: manager.alignement,
            experience: manager.experience,
            // ⟦C8 · A-120⟧ `null` (pas `undefined`) sur le défaut compat : `undefined` disparaîtrait
            // à la sérialisation JSON, et le client ne pourrait pas distinguer « pas de recrue » de
            // « champ absent d'un ancien événement ».
            manager_id: manager.id ?? null,
        },
    });
    ctx.log.push({
        turn: ctx.state.turn,
        clock_h: ctx.state.clock_h,
        famille: "evenement",
        text: `Pendant que tu étais ailleurs, ton manager tranche « ${event.nom ?? event.id} » à ta place.`,
    });
    return resoudreChoixEvenement(ctx, {
        type: "choix_evenement",
        event_id: event.id,
        choice_id: choiceId,
    });
}
// ─────────────────────────────────────────────────────────────────────────────
// Les hooks de M1.15 — à fusionner par M1.17
// ─────────────────────────────────────────────────────────────────────────────
/** Hook `effetAction` de `applyAction` pour `choix_evenement`. `true` = action traitée. */
export function effetActionEvenements(ctx, action) {
    if (action.type !== "choix_evenement")
        return false;
    resoudreChoixEvenement(ctx, action);
    return true;
}
/**
 * Jeu de hooks de M1.15. `reference` porte la référence de croissance capturée **avant**
 * l'action (voir `ReferenceCroissance`) : sans elle, l'anti-frustration ne se déclenche pas.
 *
 * ```ts
 * const hooks = hooksEvenements(capturerReference(state)); // AVANT applyAction
 * ```
 */
export function hooksEvenements(reference) {
    return {
        tirerEvenement: (ctx, moment) => {
            tirerEvenement(ctx, moment, reference ? { reference } : {});
        },
        effetAction: effetActionEvenements,
    };
}
/** Jeu de hooks sans référence de croissance (anti-frustration inerte — voir `hooksEvenements`). */
export const HOOKS_EVENEMENTS = hooksEvenements();
//# sourceMappingURL=events.js.map